<template>
  <div v-if="info && !headless" class="ngx-info">
    <h3 class="ngx-info__title">About this listing</h3>
    <p class="ngx-info__unit">
      1 token = 1 {{ info.instrument === "etf" ? "unit" : "share" }} of {{ info.name }}, listed on the
      Nigerian Exchange as {{ info.symbol }}
    </p>
    <p v-if="updatedLine" class="ngx-info__note">{{ updatedLine }}</p>
    <div v-if="rows.length" class="ngx-info__figures">
      <div v-for="row in rows" :key="row.label" class="ngx-info__row">
        <span class="ngx-info__label">{{ row.label }}</span>
        <span class="ngx-info__value" :class="row.tone">{{ row.value }}</span>
      </div>
    </div>
    <p class="ngx-info__note">
      The token price follows the NGX close every trading day. A move of more than 10% in a day, such as
      after a bonus issue, is reviewed before the price changes.
    </p>
  </div>
</template>

<script>
import api from "@/utils/api";
import { compact, currencySymbol, money, relativeTime } from "@/utils/exchange";

export default {
  name: "NgxInfo",
  props: {
    tokenId: { type: String, default: "" },
    currency: { type: String, default: "NGN" },
    // Load and emit the listing without drawing this panel: the trade page shows the same
    // figures in its cards and range bars.
    headless: { type: Boolean, default: false },
  },
  // The page uses the same listing for its price cards, so it gets it from here rather than
  // asking again.
  emits: ["loaded"],
  data() {
    return { info: null };
  },
  computed: {
    // Each row is skipped when NGX has not given its figure.
    rows() {
      const q = this.info?.quote || {};
      const naira = (n) => money(n, this.currency, 2);
      const range = (low, high) => (low && high ? `${naira(low)} – ${naira(high)}` : null);
      const change = Number(q.price_change_percent);
      return [
        Number.isFinite(change) && q.price_change_percent !== null && {
          label: "Today",
          value: `${change > 0 ? "+" : change < 0 ? "-" : ""}${Math.abs(change).toFixed(2)}%`,
          tone: change > 0 ? "up" : change < 0 ? "down" : "",
        },
        { label: "Day range", value: range(q.day_low, q.day_high) },
        { label: "52-week range", value: range(q.low52wk, q.high52wk) },
        { label: "Market cap", value: q.market_cap ? `${currencySymbol(this.currency)}${compact(q.market_cap)}` : null },
        { label: "Shares in issue", value: this.info.shares_outstanding ? compact(this.info.shares_outstanding) : null },
        { label: "Sector", value: this.info.sector || null },
      ].filter((row) => row && row.value);
    },
    updatedLine() {
      const at = this.info?.quote?.last_updated;
      return at ? `NGX close, updated ${relativeTime(at)}` : "";
    },
  },
  watch: {
    tokenId: {
      immediate: true,
      handler(id) {
        this.load(id);
      },
    },
  },
  methods: {
    // Any failure, including the 404 for a deal with no NGX listing, just hides the block.
    async load(id) {
      this.info = null;
      if (!id) return;
      try {
        const res = await api.getNgxToken(id);
        const payload = res?.data?.data;
        if (this.tokenId === id && payload?.symbol) {
          this.info = payload;
          this.$emit("loaded", payload);
        }
      } catch (e) {
        this.info = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$border-color: #e2e8f0;

.ngx-info {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 8px;
  }

  &__unit {
    font-size: 15px;
    font-weight: 500;
    color: $text-primary;
    margin: 0 0 6px;
  }

  &__figures {
    border-top: 1px solid $border-color;
    margin: 12px 0;
    padding-top: 12px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 1.8;
  }

  &__label {
    color: $text-secondary;
  }

  &__value {
    color: $text-primary;
    font-weight: 500;
    text-align: right;

    &.up {
      color: #4d84ff;
    }

    &.down {
      color: #e84343;
    }
  }

  &__note {
    font-size: 13px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 0 0 6px;
  }
}
</style>
